import React, { useState, useEffect, useRef } from "react";
import jwtDecode from "jwt-decode";
import ChatServices from "../../../services/chat.services";
import { getCurrentTimeWithAMPM } from "../../../helpers/DateTimeFormatter";
import { CHAT_API_BASE_URL } from "../../../config";
import { useLocation } from "react-router-dom";
import {
  sliceAndEllipsis,
  sliceStringHandler,
} from "../../../helpers/StringSlicer";
import { useMessageCount } from "../../../MessageContext";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import useChatNotificationService from "../../../hooks/useChatNotificationService";
import { isAdminRole } from "../../../utils/roleUtils";

const SupportTabContent = () => {
  const { incrementNewMessageCount, allChats } = useMessageCount();
  const [messages, setMessages] = useState([]);
  const [currentChatDetails, setCurrentChatDetails] = useState({});
  const [textMessage, setTextMessage] = useState("");
  const stompClientRef = useRef(null);
  const isConnecting = useRef(false);
  const [supportChat, setSupportChat] = useState(null);
  const location = useLocation();
  // const sellerResponse = location.state?.data || {}; // Default to an empty object
  const [sellerResponse, setSellerResponse] = useState(
    location.state?.data || null
  );
  const { getAllChatsRecentHandler } = useChatNotificationService();

  useEffect(() => {
    if (allChats) {
      const foundSupport = allChats.find(chat => chat.productId === 0 || chat.type?.toLowerCase() === 'support');
      setSupportChat(foundSupport);
    }
  }, [allChats]);

  const token = localStorage.getItem("user");
  const decoded = token ? jwtDecode(token) : null;
  const UserId = decoded?.sub;
  const userName = decoded?.name;
  const imageUrl = decoded?.imageUrl || "../images/profile-picture.webp";
  const isAdmin = isAdminRole(decoded);
  const containerRef = useRef(null);
  const connect = () => {
    if (isConnecting.current) return;
    if (stompClientRef.current && stompClientRef.current.connected) return;

    isConnecting.current = true;
    const Stomp = require("stompjs");
    var SockJS = require("sockjs-client");
    const socket = new SockJS(CHAT_API_BASE_URL);
    const client = Stomp.over(socket);

    client.debug = null;

    const onConnectedLocal = () => {
      isConnecting.current = false;
      stompClientRef.current = client;
      client.subscribe(
        "/user/" + UserId + "/queue/messages",
        onMessageReceived
      );
    };

    const onErrorLocal = (err) => {
      isConnecting.current = false;
      console.error("STOMP connection error:", err);
    };

    client.connect({}, onConnectedLocal, onErrorLocal);
  };

  const onMessageReceived = (msg) => {
    const message = JSON.parse(msg.body);
    if (message.senderId !== Number(UserId)) {
      setMessages((prevMessages) => {
        const alreadyExists = prevMessages.some((item) => item.id === message.id);
        if (alreadyExists) return prevMessages;
        return [...prevMessages, message];
      });

      // If the message is from the currently active chat, mark it as read in the DB immediately
      if (Number(message.senderId) === Number(currentChatDetails?.userSummary?.id)) {
        ChatServices.markAsRead(
          message.senderId,
          message.recipientId,
          message.productId ?? 0
        ).catch(err => console.error("Real-time markAsRead failed", err));
      }

      setTimeout(() => {
        scrollToBottom();
      }, 500);
      getAllChatsRecentHandler(); // Refresh global badge
    }
  };

  const scrollToBottom = () => {
    // Scroll to the bottom of the container
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  };
  const sendMessage = () => {
    if (textMessage.trim() === "") return;

    if (stompClientRef.current && stompClientRef.current.connected) {
      const chatMessage = {
        senderId: Number(UserId),
        senderName: isAdmin ? "ADMIN" : userName,
        recipientId:
          currentChatDetails?.userSummary?.id ||
          sellerResponse?.userResponse?.id,
        recipientName:
          currentChatDetails?.userSummary?.name ||
          sellerResponse?.userResponse?.name,
        content: textMessage,
        timestamp: new Date(),
        productId: 0,
        productTitle: "General Support",
      };
      stompClientRef.current.send("/app/chat", {}, JSON.stringify(chatMessage));
      setMessages([...messages, chatMessage]);
      setTextMessage("");
      getAllChatsRecentHandler();
      setTimeout(() => {
        scrollToBottom();
      }, 1000);
    } else {
      if (isConnecting.current) {
        toast.info("Connecting to chat... please wait.", {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 2000,
        });
      } else {
        toast.warning("Connection lost. Trying to reconnect...", {
          position: toast.POSITION.TOP_RIGHT,
        });
        connect();
      }
    }
  };

  const getAllChatsHandlerAndOpen = async () => {
    try {
      await getAllChatsRecentHandler();
    } catch (error) { }
  };


  const [activeItem, setActiveItem] = useState(null);

  const openMessagesHandler = async (item) => {
    setActiveItem(item?.recentMessage?.chatId);
    if (item) {
      let recipientId = item?.userSummary?.id;
      try {
        let res = await ChatServices.getMessagesSupport(
          Number(UserId),
          recipientId
        );
        setMessages(res.data.messagesList || res.data);
        setCurrentChatDetails(item);
        // Small delay to ensure backend mark-as-read transaction commits
        // before we refetch the updated unread counts
        setTimeout(async () => {
          await getAllChatsRecentHandler();
        }, 500);
        setTimeout(() => {
          scrollToBottom();
        }, 1000);
      } catch (error) {
        return error;
      }
    }
  };

  useEffect(() => {
    connect();
    getAllChatsRecentHandler();
  }, []);

  // Effect to handle opening the support chat once it's found in allChats
  useEffect(() => {
    if (supportChat && !currentChatDetails?.userSummary) {
      openMessagesHandler(supportChat);
    }
  }, [supportChat]);

  return (
    <div
      className="tab-pane"
      id="support"
      role="tabpanel"
      aria-labelledby="support-tab"
    >
      <div className="row">
        <div className="col-lg-6 p-right ">
          {isAdmin ? (
            <div className="row">
              <div className="col-lg-12">
                <div className="no-chat-message">
                  <img src="./img/speech-bubble.png" alt="chat-icon" />
                  <h3 className="text_no">Admin Support</h3>
                  <p className="text_no">
                    Use the Admin Dashboard to manage support conversations.
                  </p>
                </div>
              </div>
            </div>
          ) : !supportChat ? (
            <div className="row">
              <div className="col-lg-12">
                <div className="no-chat-message">
                  <img src="./img/speech-bubble.png" alt="chat-icon" />
                  <h3 className="text_no">No Chat messages</h3>
                  <p className="text_no">
                    Start conversing to see your message here!
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <ul className="chat-user">
              <li
                key={supportChat?.id}
                onClick={() => openMessagesHandler(supportChat)}
                style={{ cursor: "pointer" }}
                className={
                  activeItem === supportChat?.recentMessage?.chatId
                    ? "active-item"
                    : ""
                }
              >
                <div className="min-cht-div">
                  <div className="user-img">
                    <img
                      src={
                        supportChat?.userSummary?.profilePicture ||
                        "../images/profile-picture.webp"
                      }
                      alt="profile image"
                    />
                  </div>
                  <div className="user-text">
                    <h3 className="users-chat">
                      ADMIN
                      {supportChat?.messageCount !== 0 && (
                        <span className="msgCount">
                          {supportChat?.messageCount}
                        </span>
                      )}
                    </h3>
                    <h3 style={{ fontSize: "15px", fontWeight: "500" }}>
                      {supportChat?.recentMessage?.productTitle}
                    </h3>
                    <span className="time-show">
                      {" "}
                      {getCurrentTimeWithAMPM(
                        supportChat?.recentMessage?.timestamp
                      )}
                    </span>

                    <p>
                      {sliceStringHandler(supportChat?.recentMessage?.content)}
                    </p>
                  </div>
                </div>
              </li>
            </ul>
          )}
        </div>
        {isAdmin || !supportChat || !currentChatDetails?.userSummary ? null : (
          <div className="col-lg-6 p-left ">
            <div className="main-chat-box">
              <div className="chat-show-box">
                <div className="user-name">
                  <div
                    className="msg-img"
                    style={{
                      backgroundImage: `url(${currentChatDetails?.userSummary?.profilePicture ||
                        sellerResponse?.userResponse?.imageUrl ||
                        "../images/profile-picture.webp"
                        })`,
                    }}
                  ></div>
                  <h3 className="main-user-ct">
                    ADMIN
                  </h3>
                </div>
                <div className="user-detail">
                  <a
                    href={`tel:${currentChatDetails?.userSummary?.mobile ||
                      sellerResponse?.userResponse?.mobile
                      }`}
                  >
                    <i className="fa fa-phone"></i>
                  </a>
                  <a
                    href={`mailto:${currentChatDetails?.userSummary?.email ||
                      sellerResponse?.userResponse?.email
                      }`}
                  >
                    <i className="fa fa-envelope"></i>
                  </a>
                </div>
              </div>
              <div
                style={{ borderBottom: "2px solid #DADBDB", margin: "10px 0" }}
              >
                <h5 style={{ marginLeft: "2rem", paddingBottom: "4px" }}>
                  {currentChatDetails?.recentMessage?.productTitle ||
                    sellerResponse?.title}
                </h5>
              </div>
              <div className="chat-message" ref={containerRef}>
                {messages?.map((item) => (
                  <div
                    className={`msg ${item.senderId === Number(UserId)
                      ? "right-msg"
                      : "left-msg"
                      } mt-3`}
                  >
                    <div
                      className="msg-img"
                      style={{
                        backgroundImage:
                          item.senderId === Number(UserId)
                            ? `url(${imageUrl})`
                            : `url(${currentChatDetails?.userSummary
                              ?.profilePicture ||
                            sellerResponse?.userResponse?.imageUrl ||
                            "../images/profile-picture.webp"
                            })`,
                      }}
                    ></div>

                    <div className="msg-bubble">
                      <div className="msg-info">
                        <div className="msg-info-time">
                          {getCurrentTimeWithAMPM(item?.timestamp)}
                        </div>
                      </div>

                      <div
                        className="msg-text"
                        style={{ wordWrap: "break-word" }}
                      >
                        {item?.content}
                      </div>
                    </div>
                  </div>
                ))}
                <div className="msger-inputarea">
                  <input
                    type="text"
                    className="msger-input"
                    placeholder="Enter your message..."
                    data-last-active-input=""
                    value={textMessage}
                    onChange={(e) => setTextMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        sendMessage();
                      }
                    }}
                  />
                  <button
                    onClick={sendMessage}
                    type="button"
                    className="msger-send-btn"
                  >
                    Send
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupportTabContent;
